import { Link, Navigate } from "react-router-dom";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { OrganisationSettingsForm } from "@/components/OrganisationSettingsForm";
import { EInvoicingSettings, E_INVOICING_INTRO } from "@/components/EInvoicingSettingsPage";
import { AccountingSettings } from "@/components/AccountingSettings";
import { useModules } from "@/auth/ModulesContext";
import { ACCOUNTING_CONNECTION_MODULES, E_INVOICING_REGISTRATION_MODULES } from "@/lib/modules";

export type OrganisationSection = "details" | "ar" | "e-invoicing" | "accounting";

/** E-invoicing registration sits here, not under Payables: the registered party is the organisation itself. */
export function OrganisationSettingsPage({ section }: { section: OrganisationSection }) {
  const { hasModule } = useModules();

  // Registration serves both e-invoicing modules; accounting serves any module posting to the books. Without one,
  // redirect rather than render a page of 403s; `replace` keeps it out of the back stack.
  if (
    (section === "e-invoicing" && !hasModule(E_INVOICING_REGISTRATION_MODULES)) ||
    (section === "accounting" && !hasModule(ACCOUNTING_CONNECTION_MODULES))
  ) {
    return <Navigate to="/organisation/settings" replace />;
  }

  switch (section) {
    case "details":
      return (
        <PageContainer size="narrow">
          <PageHeader
            eyebrow="Organisation"
            title="Details"
            subtitle="Who this organisation is: business info, address, financial profile and branding."
          />
          <OrganisationSettingsForm />
        </PageContainer>
      );
    case "ar":
      return (
        <PageContainer size="narrow">
          <PageHeader
            eyebrow="Organisation"
            title="AR settings"
            subtitle="Invoice defaults and bank details for this organisation. Each invoice can override them."
          />
          <OrganisationSettingsForm mode="ar-settings" />
        </PageContainer>
      );
    case "e-invoicing":
      return (
        <PageContainer size="narrow">
          <PageHeader eyebrow="Organisation" title="E-invoicing" subtitle={E_INVOICING_INTRO} />
          <div className="space-y-4">
            <EInvoicingSettings />
            {/* The documents live under Payables; point there for a client that can open it. */}
            {hasModule("ap.e_invoicing") && (
              <p className="text-sm text-muted-foreground">
                Documents suppliers send over the network appear under{" "}
                <Link to="/received-e-invoices" className="font-medium underline decoration-muted-foreground/40 underline-offset-4 transition-colors hover:decoration-current">
                  Payables &gt; E-invoicing
                </Link>
                .
              </p>
            )}
          </div>
        </PageContainer>
      );
    case "accounting":
      // Default width: the synced chart is a table, and reads badly squeezed into a form column.
      return (
        <PageContainer>
          <PageHeader
            eyebrow="Organisation"
            title="Accounting"
            subtitle="The accounting platform this organisation posts to, and the accounts, tax rates and tracking categories synced from it."
          />
          <AccountingSettings />
        </PageContainer>
      );
  }
}
